import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import ContactModal from "@/components/ContactModal";

const SECTIONS = [
  {
    title: "What we collect",
    body: [
      "Account data — when you sign up we store your name, email address and a bcrypt hash of your password (never the password itself).",
      "Reviews and endorsements — your star ratings, written feedback and thumbs-up votes, linked to your account so every signal has a real person behind it.",
      "Claim requests — the name, business email, role, website and message you submit when claiming a listing.",
      "Listing data — company name, category, region, tagline, website, description and maker names submitted through Add Business or Owner Studio.",
      "Uploaded media — thumbnail images and video files uploaded by Verified/Claimed owners, stored securely in managed object storage. We store the file URL and your account ID.",
      "Discover interactions — per-post like counts (linked to your account if signed in, or to your anonymous session ID if not) and view counts (session-based, not linked to personal data).",
      "Discover feature requests — business name, category, contact message and boost type you submit through 'Get Featured'. These are not published publicly until an admin approves them.",
      "Payment data — when you activate a Boost, your payment is processed by Stripe. HighRank never receives or stores your card number, CVV or full payment details; Stripe returns only a session ID and payment status.",
      "Usage signals — an anonymous visitor session ID (stored in your browser) with a periodic heartbeat so we can show a live visitor count, and per-company view counts. We do not run third-party analytics or advertising trackers.",
    ],
  },
  {
    title: "Cookies and local storage",
    body: [
      "access_token — an httpOnly cookie that keeps you signed in. It cannot be read by JavaScript and expires after 7 days.",
      "hr-theme — your light/dark preference, stored in your browser only.",
      "hr-visitor-session — a random ID used solely for the live visitor counter and anonymous Discover like/view tracking.",
      "We do not use advertising or cross-site tracking cookies.",
    ],
  },
  {
    title: "How we use your data",
    body: [
      "To operate the leaderboard: displaying listings, reviews, endorsements, Rank Positions and HighRank Scores.",
      "To operate Discover: displaying featured business cards, tracking view and like counts, and processing feature requests submitted for editorial review.",
      "To generate share images: when a Discover card is shared, we use the post's public business name, tagline, category and media thumbnail to produce a branded 1200×630 PNG. No personal account data is embedded in these images.",
      "To verify claim requests and to email you the outcome (approved or rejected) via our transactional email provider.",
      "To notify business owners of media decisions (image or video approved or rejected) via transactional email.",
      "To process Boost payments through Stripe and to activate or deactivate Boost placement based on subscription status.",
      "To process refunds: if submitted media is rejected during admin review, your Boost payment is refunded via Stripe. Refund status is stored alongside your payment record.",
      "To prevent abuse: login attempt throttling, file upload scanning, content moderation and one-review/one-thumbs-up-per-person-per-company enforcement.",
    ],
  },
  {
    title: "Third-party services",
    body: [
      "Object Storage — images, videos and other media files uploaded to business profiles and Discover posts are stored in managed object storage. Files are served via CDN URLs.",
      "Transactional Email (powered by Resend) — transactional emails only (claim decisions, media approval/rejection). We do not send marketing emails. Each message includes the business name you submitted.",
      "Stripe — payment processing for Boost subscriptions. Stripe operates under its own privacy policy. HighRank stores only a Stripe session ID and payment status flag.",
      "MongoDB Atlas — our primary database. Data is hosted in the cloud region selected at account setup.",
    ],
  },
  {
    title: "What we never do",
    body: [
      "We never sell your personal data.",
      "We never blend paid visibility (Boost) into the HighRank Score.",
      "We never publish your email address on public pages.",
      "We never use uploaded media for purposes other than displaying your business profile and Discover card.",
      "We never send marketing or promotional emails — only transactional messages you explicitly triggered.",
    ],
  },
  {
    title: "Your rights",
    body: [
      "You may request a copy, correction or deletion of your account data, reviews and endorsements at any time by emailing support@highrank.space.",
      "Businesses may request correction or removal of an unclaimed listing via the same address.",
      "You may request deletion of media you uploaded (GIFs, videos) by contacting us; we will remove the file from storage and the listing.",
      "If you are located in the EU/EEA, you have rights under the GDPR including access, rectification, erasure, restriction and data portability. You also have the right to lodge a complaint with your local supervisory authority.",
      "If you are located in California, you may have additional rights under the CCPA.",
    ],
  },
  {
    title: "Data retention & security",
    body: [
      "Passwords are stored only as bcrypt hashes. Sessions expire automatically after 7 days.",
      "Claim requests are retained while pending and for a reasonable audit period after a decision.",
      "Discover feature requests are retained while pending review, and for an audit period after approval or rejection.",
      "Uploaded media files are retained as long as the associated business listing exists, or until removed by the owner or admin. Rejected media may be deleted immediately after the review decision.",
      "Payment and refund records are retained for accounting and dispute-resolution purposes as required by law.",
      "Visitor heartbeat and anonymous view records expire automatically within minutes.",
      "All admin credentials are seeded once at setup and are never overwritten by automated processes.",
    ],
  },
];

export default function Privacy() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <main data-testid="privacy-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo
        title="Privacy Policy — HighRank"
        siteName="HighRank"
        description="How HighRank collects, uses and protects your data — accounts, reviews, claim requests and live visitor counts."
      />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <FadeUp>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-400 transition-colors mb-8"
          >
            <ArrowLeft size={13} /> Home
          </Link>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-3">{"// Legal"}</p>
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-wide text-slate-100 mb-3">
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-500 mb-12 flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-400" /> Last updated: September 2026
          </p>
        </FadeUp>
        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.04}>
              <section>
                <h2 className="font-heading text-lg font-bold text-slate-100 mb-3">{s.title}</h2>
                <ul className="space-y-2.5">
                  {s.body.map((line, j) => (
                    <li key={j} className="text-sm text-slate-400 leading-relaxed flex gap-3">
                      <span className="text-emerald-500 mt-0.5 shrink-0">—</span>
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.1}>
          <div className="mt-14 rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <p className="text-sm text-slate-400 leading-relaxed flex-1">
              Questions about this policy or your data?
            </p>
            <button
              data-testid="privacy-contact-btn"
              onClick={() => setContactOpen(true)}
              className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all hover:opacity-90"
              style={{ backgroundColor: "#3FE08C", color: "#000" }}
            >
              Contact us
            </button>
          </div>
          <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
        </FadeUp>
      </section>
    </main>
  );
}
